import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { TrashIcon } from '@heroicons/react/24/outline';
import { BaseModal, CollaboratorCombobox } from '@/components/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useFilaTrabalhoQuery } from '@/features/SI/hooks/data';
import type { ParticipanteOnboarding, PresencaOnboarding, RascunhoSessaoOnboarding, SessaoOnboarding } from './onboardingPresenca';
import { useOnboardingSessaoMutations } from './useOnboardingSessoes';

interface SessaoOnboardingModalProps {
  documentoId: string;
  versao: number;
  /** Editando uma sessão já registrada; sem isso, registra uma nova. */
  sessao?: SessaoOnboarding | null;
  /** Aberto sozinho ao fechar a apresentação: data e horários já vêm preenchidos. */
  rascunho?: RascunhoSessaoOnboarding | null;
  /** Quem já participou de tudo em alguma sessão — fica fora das sugestões. */
  jaCompletaram?: string[];
  isOpen: boolean;
  onClose: () => void;
}

const hoje = () => new Date().toLocaleDateString('sv-SE');
const DIAS_ADMISSAO_RECENTE = 45;

/** "Entrou depois" e "Saiu antes" podem ir juntos; "Participou de tudo" e "Não veio" limpam os horários. */
function alternar(participante: ParticipanteOnboarding, opcao: 'completa' | 'entrou' | 'saiu' | 'faltou'): ParticipanteOnboarding {
  if (opcao === 'completa' || opcao === 'faltou') return { ...participante, presenca: opcao, entrouAs: null, saiuAs: null };
  const entrouAs = opcao === 'entrou' ? (participante.entrouAs === null ? '' : null) : participante.entrouAs;
  const saiuAs = opcao === 'saiu' ? (participante.saiuAs === null ? '' : null) : participante.saiuAs;
  const presenca: PresencaOnboarding = entrouAs === null && saiuAs === null ? 'completa' : 'parcial';
  return { ...participante, presenca, entrouAs, saiuAs };
}

const OPCOES: Array<{ id: 'completa' | 'entrou' | 'saiu' | 'faltou'; label: string; ativa: string }> = [
  { id: 'completa', label: 'Participou de tudo', ativa: 'bg-green-100 text-green-800' },
  { id: 'entrou', label: 'Entrou depois', ativa: 'bg-amber-100 text-amber-800' },
  { id: 'saiu', label: 'Saiu antes', ativa: 'bg-amber-100 text-amber-800' },
  { id: 'faltou', label: 'Não veio', ativa: 'bg-red-100 text-red-800' },
];

function opcaoAtiva(participante: ParticipanteOnboarding, opcao: (typeof OPCOES)[number]['id']): boolean {
  if (opcao === 'completa') return participante.presenca === 'completa';
  if (opcao === 'faltou') return participante.presenca === 'faltou';
  if (opcao === 'entrou') return participante.entrouAs !== null;
  return participante.saiuAs !== null;
}

/**
 * Registrar (ou corrigir) uma sessão de onboarding: quando, quem apresentou e a presença de cada
 * participante — participou de tudo, entrou depois, saiu antes (os dois juntos valem) ou não
 * veio. Atalho pras admissões recentes (o onboarding é no 2º dia pós-admissão).
 */
export default function SessaoOnboardingModal({
  documentoId,
  versao,
  sessao,
  rascunho,
  jaCompletaram = [],
  isOpen,
  onClose,
}: SessaoOnboardingModalProps) {
  const { user } = useAuth();
  const { salvar } = useOnboardingSessaoMutations(documentoId);
  const { data: solicitacoes = [] } = useFilaTrabalhoQuery();
  const [data, setData] = useState(sessao?.data ?? rascunho?.data ?? hoje());
  const [inicio, setInicio] = useState(sessao?.inicio ?? rascunho?.inicio ?? '');
  const [fim, setFim] = useState(sessao?.fim ?? rascunho?.fim ?? '');
  const [participantes, setParticipantes] = useState<ParticipanteOnboarding[]>(sessao?.participantes ?? []);
  const apresentador = sessao?.apresentador ?? { id: user?.collaboratorId ?? user?.uid ?? '', name: user?.displayName ?? user?.email ?? '' };

  const adicionar = (pessoa: { id: string; name: string }) =>
    setParticipantes((atual) =>
      atual.some((p) => p.colaboradorId === pessoa.id)
        ? atual
        : [...atual, { colaboradorId: pessoa.id, nome: pessoa.name, presenca: 'completa', entrouAs: null, saiuAs: null, observacao: '' }]
    );
  const atualizar = (colaboradorId: string, mudar: (p: ParticipanteOnboarding) => ParticipanteOnboarding) =>
    setParticipantes((atual) => atual.map((p) => (p.colaboradorId === colaboradorId ? mudar(p) : p)));

  // Admissões dos últimos dias, já vinculadas a um colaborador e ainda fora da lista.
  const admissoesRecentes = useMemo(() => {
    const limite = Date.now() - DIAS_ADMISSAO_RECENTE * 24 * 60 * 60 * 1000;
    return solicitacoes
      .filter(
        (s) =>
          s.tipoSolicitacao === 'admissao' &&
          s.colaboradorId &&
          s.status !== 'cancelada' &&
          s.status !== 'excluida' &&
          (s.createdAt?.toMillis?.() ?? 0) >= limite
      )
      .map((s) => ({ id: s.colaboradorId as string, name: s.colaboradorNome }))
      .filter((pessoa, indice, lista) => lista.findIndex((x) => x.id === pessoa.id) === indice)
      .filter((pessoa) => !participantes.some((p) => p.colaboradorId === pessoa.id))
      .filter((pessoa) => !jaCompletaram.includes(pessoa.id));
  }, [solicitacoes, participantes, jaCompletaram]);

  const valido = Boolean(data) && participantes.length > 0;

  const handleSalvar = () => {
    salvar.mutate(
      {
        id: sessao?.id,
        documentoId,
        versao: sessao?.versao ?? versao,
        data,
        inicio,
        fim,
        apresentador,
        // Horário vazio ('') = entrou depois/saiu antes sem saber a hora — fica registrado assim.
        participantes: participantes.map((p) => ({ ...p, observacao: p.observacao.trim() })),
      },
      { onSuccess: onClose }
    );
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={sessao ? 'Editar sessão de onboarding' : 'Registrar sessão de onboarding'}
      maxWidth="max-w-4xl"
      fullHeight={false}
      actions={
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {rascunho && !sessao ? 'Não registrar' : 'Cancelar'}
          </button>
          <button type="button" className="btn-primary" disabled={!valido || salvar.isPending} onClick={handleSalvar}>
            {salvar.isPending ? 'Salvando...' : 'Salvar sessão'}
          </button>
        </div>
      }
    >
      <div className="space-y-5 py-4">
        {rascunho && !sessao && (
          <p className="rounded-lg bg-primary-50 px-4 py-3 text-sm text-primary-900">
            A apresentação ficou aberta {rascunho.inicio}–{rascunho.fim}. Se foi uma sessão, adicione quem participou e salve. Se
            foi estudo, revisão do material ou ficou aberta por engano, é só clicar em &quot;Não registrar&quot;.
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-4">
          <label className="block text-sm text-gray-700">
            Data
            <input type="date" className="input-gaveta mt-1" value={data} onChange={(e) => setData(e.target.value)} />
          </label>
          <label className="block text-sm text-gray-700">
            Início
            <input type="time" className="input-gaveta mt-1" value={inicio} onChange={(e) => setInicio(e.target.value)} />
          </label>
          <label className="block text-sm text-gray-700">
            Fim
            <input type="time" className="input-gaveta mt-1" value={fim} onChange={(e) => setFim(e.target.value)} />
          </label>
          <div className="text-sm text-gray-700">
            Apresentado por
            <p className="mt-1 rounded-lg bg-gray-50 px-3 py-2.5 text-[15px] text-gray-900">{apresentador.name}</p>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Participantes</p>
          <CollaboratorCombobox
            selectedId={null}
            onChange={(colaborador) => colaborador && adicionar({ id: colaborador.id, name: colaborador.name })}
            placeholder="Buscar colaborador pelo nome..."
          />
          {admissoesRecentes.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-gray-600">Admissões recentes:</span>
              {admissoesRecentes.map((pessoa) => (
                <button
                  key={pessoa.id}
                  type="button"
                  onClick={() => adicionar(pessoa)}
                  className="rounded-full border border-dashed border-primary-300 bg-primary-50 px-3 py-1 text-primary-700 hover:bg-primary-100"
                >
                  + {pessoa.name}
                </button>
              ))}
              {admissoesRecentes.length > 1 && (
                <button
                  type="button"
                  onClick={() => admissoesRecentes.forEach(adicionar)}
                  className="rounded-full border border-gray-200 bg-white px-3 py-1 text-gray-700 hover:bg-gray-50"
                >
                  Adicionar todas
                </button>
              )}
            </div>
          )}
        </div>

        {participantes.length === 0 ? (
          <p className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
            Nenhum participante ainda — busque pelo nome ou use as admissões recentes.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {participantes.map((p) => (
              <li key={p.colaboradorId} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <span className="min-w-[10rem] flex-1 font-medium text-gray-900">{p.nome}</span>
                <span role="group" aria-label={`Presença de ${p.nome}`} className="inline-flex flex-wrap gap-0.5 rounded-lg border border-gray-200 p-0.5">
                  {OPCOES.map((opcao) => (
                    <button
                      key={opcao.id}
                      type="button"
                      aria-pressed={opcaoAtiva(p, opcao.id)}
                      onClick={() => atualizar(p.colaboradorId, (atual) => alternar(atual, opcao.id))}
                      className={clsx(
                        'rounded-md px-2.5 py-1 text-xs font-semibold',
                        opcaoAtiva(p, opcao.id) ? opcao.ativa : 'text-gray-500 hover:bg-gray-50'
                      )}
                    >
                      {opcao.label}
                    </button>
                  ))}
                </span>
                {p.entrouAs !== null && (
                  <label className="flex items-center gap-1.5 text-sm text-gray-700">
                    entrou às
                    <input
                      type="time"
                      className="input-gaveta w-28 py-1.5"
                      value={p.entrouAs}
                      onChange={(e) => atualizar(p.colaboradorId, (atual) => ({ ...atual, entrouAs: e.target.value }))}
                    />
                  </label>
                )}
                {p.saiuAs !== null && (
                  <label className="flex items-center gap-1.5 text-sm text-gray-700">
                    saiu às
                    <input
                      type="time"
                      className="input-gaveta w-28 py-1.5"
                      value={p.saiuAs}
                      onChange={(e) => atualizar(p.colaboradorId, (atual) => ({ ...atual, saiuAs: e.target.value }))}
                    />
                  </label>
                )}
                <input
                  className="input-gaveta min-w-[10rem] flex-1 py-1.5 text-sm"
                  placeholder="Observação (opcional)"
                  aria-label={`Observação de ${p.nome}`}
                  value={p.observacao}
                  onChange={(e) => atualizar(p.colaboradorId, (atual) => ({ ...atual, observacao: e.target.value }))}
                />
                <button
                  type="button"
                  aria-label={`Tirar ${p.nome} da sessão`}
                  onClick={() => setParticipantes((atual) => atual.filter((x) => x.colaboradorId !== p.colaboradorId))}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-red-600"
                >
                  <TrashIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </BaseModal>
  );
}
